import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  requestPasswordReset, verifyPasswordResetOtp, confirmPasswordReset,
} from '@/services/rbac-auth.service';
import { ROUTES } from '@/constants';

export type ForgotStep = 'email' | 'otp' | 'new-password';

export function useForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState<ForgotStep>('email');
  const [identifier, setIdentifier] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const submitEmail = useCallback(async (id: string) => {
    setError(null); setLoading(true); setIdentifier(id);
    try { await requestPasswordReset(id); } catch { /* anti-enumeración: ignorar */ }
    finally { setStep('otp'); setLoading(false); }
  }, []);

  const resend = useCallback(async () => {
    try { await requestPasswordReset(identifier); } catch { /* anti-enumeración */ }
  }, [identifier]);

  const submitOtp = useCallback(async (otpCode: string) => {
    setError(null); setLoading(true);
    try {
      const { resetToken: rt } = await verifyPasswordResetOtp(identifier, otpCode);
      setResetToken(rt); setStep('new-password');
    } catch { setError('Código inválido o expirado.'); }
    finally { setLoading(false); }
  }, [identifier]);

  const submitNewPassword = useCallback(async (newPassword: string) => {
    setError(null); setLoading(true);
    try {
      await confirmPasswordReset(identifier, resetToken, newPassword);
      navigate(ROUTES.LOGIN, { replace: true });
    } catch { setError('No se pudo actualizar la contraseña. Reintentá.'); }
    finally { setLoading(false); }
  }, [identifier, resetToken, navigate]);

  return { step, identifier, error, loading, submitEmail, submitOtp, submitNewPassword, resend };
}
